/**
 * Sheet `document-sheet` — Aufnehmen **und** Ändern in einer Komponente
 * (Spec 09, „Felder in Reihenfolge"/„Aktionen"), dieselbe Bauform wie
 * `features/tasks/TaskSheet.tsx`. `document === null` heißt Aufnehmen.
 *
 * **„Hängt an" ist zweierlei**, je nachdem, ob das Objekt feststeht:
 * - `fixedSubjectId` gesetzt (Reiter *Dokumente* eines Objekts, immer beim
 *   Aufnehmen dort) **oder** ein bereits zugeordnetes Dokument wird geändert
 *   (`document.subject !== null`) → fester Text (`document-subject`,
 *   `ARCHITECTURE.md` 6.4: „aus dem Kontext anlegen"; ein zugeordnetes
 *   Dokument hat in 09 keinen Weg, umzuhängen — `move` lehnt Dokumente
 *   ausdrücklich ab, Spec 09 „Zuordnen ist editor, nicht manager").
 * - sonst (Bereich *Dokumente* beim Aufnehmen, oder ein noch nicht
 *   zugeordnetes Dokument wird geändert) → `document-subject-picker` mit
 *   der Vorbelegung „Eingang". `document-assign-<id>` in `DocumentRow.tsx`
 *   öffnet **dasselbe** Sheet — es ist kein zweiter Vorgang, sondern
 *   derselbe Änderungs-Dialog an einem noch nicht zugeordneten Dokument.
 *
 * Ein Zuordnungswechsel geht über `POST /documents/{id}/assign`, nie über
 * `PATCH` (das Schema kennt kein `subject_id`) — beim Speichern läuft
 * `assign` deshalb **vor** einem etwaigen `PATCH` der übrigen Felder, mit
 * der von `assign` zurückgegebenen `version` als `If-Match` für den zweiten
 * Aufruf.
 */

import { useEffect, useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import type { EntitySelection } from '@/components/EntityPicker'
import { EnumSelectField } from '@/components/EnumSelectField'
import { FIELD_CLASS, FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { ApiError } from '@/lib/api'
import { centsToDecimalInput } from '@/lib/money'
import {
  useAcceptMeterSuggestion,
  useAcceptSuggestion,
  useArchiveDocument,
  useAssignDocument,
  useCreateComment,
  useDeleteComment,
  useDismissMeterSuggestion,
  useDismissSuggestion,
  useDocumentCategories,
  useDocumentComments,
  useDocumentDownloadUrl,
  useDocumentLimits,
  useUpdateDocument,
} from '@/features/documents/api'
import type { DocumentRow, SenderRow } from '@/features/documents/api'
import { DocumentComments } from '@/features/documents/DocumentComments'
import { DocumentSubjectField, EINGANG_SELECTION } from '@/features/documents/DocumentSubjectField'
import { buildDocumentPatchBody } from '@/features/documents/document-form'
import type { DocumentFormValues } from '@/features/documents/document-form'
import { documentPreviewLabel, resolveDocumentPreviewAction } from '@/features/documents/document-preview'
import { documentSearchabilityHint, documentSenderDisplay, documentSubjectLabel } from '@/features/documents/labels'
import { MeterSuggestionCard } from '@/features/documents/MeterSuggestionCard'
import { SenderField } from '@/features/documents/SenderField'
import type { SenderSelection } from '@/features/documents/SenderField'
import { SenderSheet } from '@/features/documents/SenderSheet'
import { SuggestionCard } from '@/features/documents/SuggestionCard'
import { useDocumentUpload } from '@/features/documents/useDocumentUpload'

interface DocumentSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly fixedSubjectId?: string
  readonly fixedSubjectLabel?: string
  /** `null` für Aufnehmen, sonst das zu ändernde Dokument. */
  readonly document: DocumentRow | null
  readonly onUploadStart: (fileName: string) => void
  readonly onUploadSettled: () => void
}

const EMPTY_FORM: DocumentFormValues = {
  title: '',
  documentDate: '',
  amount: '',
  category: '',
  direction: 'incoming',
}

function deriveEditForm(document: DocumentRow): DocumentFormValues {
  return {
    title: document.title,
    documentDate: document.document_date ?? '',
    amount: document.amount_cents === null ? '' : centsToDecimalInput(document.amount_cents),
    category: document.category?.value ?? '',
    direction: document.direction.value,
  }
}

function deriveSubjectSelection(document: DocumentRow | null): EntitySelection {
  if (document?.subject == null) return EINGANG_SELECTION
  return { id: document.subject.id, label: documentSubjectLabel(document.subject) }
}

/** Spec 24c, Oberfläche B: `sender_ref` ist die Auswahl; ohne ihn bleibt das
 * Feld auf „Kein Absender" (die Karte in `SenderField` zeigt Altbestand über
 * `legacyText` separat). */
function deriveSenderSelection(document: DocumentRow | null): SenderSelection | null {
  if (document?.sender_ref == null) return null
  return { id: document.sender_ref.id, label: document.sender_ref.label }
}

function filenameWithoutExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot <= 0 ? name : name.slice(0, dot)
}

export function DocumentSheet({
  open,
  onOpenChange,
  fixedSubjectId,
  fixedSubjectLabel,
  document,
  onUploadStart,
  onUploadSettled,
}: DocumentSheetProps) {
  const isEdit = document !== null
  const titleRef = useRef<HTMLInputElement>(null)

  const categories = useDocumentCategories()
  // AK-500, W-B4: die Richtlinie kommt vom Server statt als Literal hier zu
  // stehen — `accept` am Dateifeld unten ist die einzige Verwendung.
  const limits = useDocumentLimits()
  const { upload, isUploading } = useDocumentUpload()
  const updateMutation = useUpdateDocument()
  const assignMutation = useAssignDocument()
  const archiveMutation = useArchiveDocument()
  const downloadUrlMutation = useDocumentDownloadUrl()
  const acceptSuggestionMutation = useAcceptSuggestion()
  const dismissSuggestionMutation = useDismissSuggestion()
  const acceptMeterSuggestionMutation = useAcceptMeterSuggestion()
  const dismissMeterSuggestionMutation = useDismissMeterSuggestion()
  const createCommentMutation = useCreateComment()
  const deleteCommentMutation = useDeleteComment()

  const [file, setFile] = useState<File | null>(null)
  const [form, setForm] = useState<DocumentFormValues>(EMPTY_FORM)
  const [initialForm, setInitialForm] = useState<DocumentFormValues>(EMPTY_FORM)
  const [subjectSelection, setSubjectSelection] = useState<EntitySelection>(EINGANG_SELECTION)
  const [initialSubjectId, setInitialSubjectId] = useState('')
  // Spec 24c, Oberfläche B: die Absenderauswahl ist getrennt vom übrigen
  // Formular verfolgt (wie `subjectSelection`), weil ihr Server-Feld
  // (`sender_id`) nur dann in den Rumpf soll, wenn das Feld angefasst wurde —
  // ein Altbestand mit reinem Freitext bliebe sonst beim bloßen Speichern
  // anderer Felder unangetastet erhalten (Fachregel 7 gilt nur, wenn `sender_
  // id` wirklich gesendet wird). `senderTouched` hält genau das fest.
  const [senderSelection, setSenderSelection] = useState<SenderSelection | null>(null)
  const [senderTouched, setSenderTouched] = useState(false)
  const [legacyText, setLegacyText] = useState<string | null>(null)
  const [senderSheetState, setSenderSheetState] = useState<{ readonly searchText: string } | null>(null)
  // Das Blatt spiegelt nach Übernehmen/Verwerfen die frische Zeile, ohne zu
  // schließen (Spec 24c, Oberfläche A: „bleibt das Blatt offen") — die
  // ursprüngliche `document`-Prop bleibt dafür stehen, `DocumentList` weiß
  // nichts von der Übernahme, bis sie über `keys.documents.all` neu lädt.
  const [activeDocument, setActiveDocument] = useState<DocumentRow | null>(document)
  const [suggestionNotice, setSuggestionNotice] = useState<string | undefined>(undefined)
  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)
  const [uploadFailed, setUploadFailed] = useState(false)
  // W-D5 (`reports/18-design-reviewer.md`): Grundstück, Gebäude und Einheit
  // fragen vor dem Archivieren zurück, das Dokument-Sheet archivierte bisher
  // sofort — und ohne Kaskaden-Vorschau (ein Dokument hat keine Kinder) ist
  // `ConfirmationStrip` die passende, bereits vorhandene Bauform dafür, nicht
  // das schwerere `ArchiveControls` mit `archive-preview`.
  const [isArchiveConfirmPending, setIsArchiveConfirmPending] = useState(false)

  // Spec 44: Kommentare gibt es nur an einem bestehenden Dokument. Externe
  // bekommen 403 — dann entfällt der Abschnitt, es ist kein Fehler.
  const commentsDocumentId = activeDocument?.id ?? ''
  const comments = useDocumentComments(commentsDocumentId, { enabled: open && isEdit })
  const isCommentsForbidden = comments.error instanceof ApiError && comments.error.isForbidden

  /** Übernimmt eine frische Dokumentzeile in jeden abgeleiteten Zustand —
   * beim Öffnen **und** nach Übernehmen/Verwerfen (dieselbe Ableitung, kein
   * zweiter Weg). */
  function applyDocumentSnapshot(nextDocument: DocumentRow | null) {
    const nextForm = nextDocument !== null ? deriveEditForm(nextDocument) : EMPTY_FORM
    setForm(nextForm)
    setInitialForm(nextForm)
    const selection = deriveSubjectSelection(nextDocument)
    setSubjectSelection(selection)
    setInitialSubjectId(selection.id)
    setSenderSelection(deriveSenderSelection(nextDocument))
    setSenderTouched(false)
    const senderDisplay = nextDocument !== null ? documentSenderDisplay(nextDocument) : null
    setLegacyText(senderDisplay?.isFreetext === true ? senderDisplay.label : null)
    setActiveDocument(nextDocument)
  }

  // Jedes Öffnen beginnt frisch — dasselbe React-Muster wie `TaskSheet`
  // („Adjusting state when a prop changes", während des Renderns statt in
  // einem Effekt).
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      applyDocumentSnapshot(document)
      setFile(null)
      setIsMoreOpen(
        document !== null &&
          (document.sender !== null ||
            document.document_date !== null ||
            document.amount_cents !== null ||
            document.category !== null),
      )
      setSenderSheetState(null)
      setSuggestionNotice(undefined)
      setFieldError(undefined)
      setGeneralError(undefined)
      setUploadFailed(false)
      setIsArchiveConfirmPending(false)
    }
  }

  // „Hängt an" ist wählbar, solange kein festes Objekt vorgegeben ist und
  // (beim Ändern) das Dokument noch nicht zugeordnet ist — siehe Kopf dieser
  // Datei. Als Ternary statt `document === null || document.subject ===
  // null`: `@typescript-eslint/prefer-optional-chain` schlägt dafür
  // `document?.subject === null` vor, das bei `document === null` `false`
  // statt des hier gewollten `true` ergäbe (`undefined === null` ist in
  // JavaScript `false`) — eine falsche Vereinfachung, keine gleichwertige.
  // Basis ist `activeDocument`, nicht die `document`-Prop: Übernehmen kann
  // das Objekt setzen, danach ist „Hängt an" fester Text (Oberfläche A).
  const subjectEditable =
    fixedSubjectId === undefined && (activeDocument === null ? true : activeDocument.subject === null)

  const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm) || subjectSelection.id !== initialSubjectId || senderTouched
  const hasTitle = form.title.trim() !== ''
  const canSubmit = isEdit ? isDirty && hasTitle : file !== null && hasTitle && !isUploading
  const isPending = isUploading || updateMutation.isPending || assignMutation.isPending

  function updateField(name: keyof DocumentFormValues) {
    return (value: string) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  function handleFile(selected: File) {
    setFile(selected)
    setForm((current) => (current.title.trim() === '' ? { ...current, title: filenameWithoutExtension(selected.name) } : current))
    titleRef.current?.focus()
  }

  function resetForAnother() {
    setFile(null)
    setForm((current) => ({ ...current, title: '' }))
    setInitialForm((current) => ({ ...current, title: '' }))
    setFieldError(undefined)
    setGeneralError(undefined)
    setUploadFailed(false)
    titleRef.current?.focus()
  }

  async function submitEdit() {
    if (activeDocument === null) return
    let ifMatch = String(activeDocument.version)

    if (subjectEditable && subjectSelection.id !== initialSubjectId) {
      const assigned = await assignMutation.mutateAsync({
        documentId: activeDocument.id,
        subjectId: subjectSelection.id,
        ifMatch,
      })
      ifMatch = String(assigned.version)
    }

    // `undefined` heißt „Feld nicht berührt" (Fachregel 7 gilt nur, wenn der
    // Rumpf `sender_id` wirklich trägt) — nicht dasselbe wie `null`.
    const senderIdPatch = senderTouched ? (senderSelection === null ? null : senderSelection.id) : undefined
    const patchBody = buildDocumentPatchBody(form, initialForm, senderIdPatch)
    if (Object.keys(patchBody).length > 0) {
      await updateMutation.mutateAsync({ documentId: activeDocument.id, body: patchBody, ifMatch })
    }
    onOpenChange(false)
  }

  async function submitCreate(andAnother: boolean) {
    if (file === null) return
    const subjectId = fixedSubjectId ?? (subjectSelection.id === '' ? undefined : subjectSelection.id)
    onUploadStart(file.name)
    setUploadFailed(false)
    try {
      await upload({ file, form, subjectId, ...(senderSelection !== null ? { senderId: senderSelection.id } : {}) })
      onUploadSettled()
      if (andAnother) resetForAnother()
      else onOpenChange(false)
    } catch (error) {
      onUploadSettled()
      setUploadFailed(true)
      throw error
    }
  }

  async function submit(andAnother: boolean) {
    if (!canSubmit || isPending) return
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      if (isEdit) {
        await submitEdit()
      } else {
        await submitCreate(andAnother)
      }
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit(false)
  }

  // `submit` schließt über `form`/`file` und ändert sich bei jedem Rendern —
  // ein Ref hält die jeweils aktuelle Fassung für den Tastenlistener unten,
  // dieselbe Bauform wie `TaskSheet.tsx`.
  const submitRef = useRef(submit)
  useEffect(() => {
    submitRef.current = submit
  })

  useEffect(() => {
    if (!open) return
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault()
        void submitRef.current(false)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  async function handleArchive() {
    if (activeDocument === null) return
    setGeneralError(undefined)
    try {
      await archiveMutation.mutateAsync({ documentId: activeDocument.id, ifMatch: String(activeDocument.version) })
      setIsArchiveConfirmPending(false)
      onOpenChange(false)
    } catch (error) {
      setIsArchiveConfirmPending(false)
      setGeneralError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  // W-B2 (`reports/18-design-reviewer.md`): kein `try/catch` bedeutete 403,
  // 404 und Netzfehler liefen als unbehandelte Rejection — kein Betrachter,
  // keine Meldung, kein Zustand. `handleArchive` oben stand als Vorlage
  // direkt daneben.
  async function handlePreview() {
    if (activeDocument === null) return
    setGeneralError(undefined)
    try {
      const signed = await downloadUrlMutation.mutateAsync(activeDocument.id)
      const action = resolveDocumentPreviewAction(activeDocument.mime_type, signed.download_url, activeDocument.title)
      if (action.kind === 'open') {
        window.open(action.url, '_blank', 'noopener,noreferrer')
        return
      }
      const link = window.document.createElement('a')
      link.href = action.url
      link.download = action.filename
      link.click()
    } catch (error) {
      setGeneralError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  /** `document-suggestion-accept` (Spec 24c, Oberfläche A): schließt das
   * Blatt nicht — die frische Zeile ersetzt `activeDocument`, „Weitere
   * Angaben" öffnet sich, damit Absender und Art ohne weiteren Klick
   * sichtbar sind. */
  async function handleAcceptSuggestion() {
    if (activeDocument === null) return
    setGeneralError(undefined)
    try {
      const result = await acceptSuggestionMutation.mutateAsync({
        documentId: activeDocument.id,
        ifMatch: String(activeDocument.version),
      })
      applyDocumentSnapshot(result.document)
      setIsMoreOpen(true)
      setSuggestionNotice(result.subject_applied ? undefined : (result.notice ?? undefined))
    } catch (error) {
      setGeneralError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  async function handleDismissSuggestion() {
    if (activeDocument === null) return
    setGeneralError(undefined)
    try {
      const result = await dismissSuggestionMutation.mutateAsync({
        documentId: activeDocument.id,
        ifMatch: String(activeDocument.version),
      })
      applyDocumentSnapshot(result)
      setSuggestionNotice(undefined)
    } catch (error) {
      setGeneralError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  /** `document-meter-suggestion-accept` (Spec 46c, Regel 13): hängt das
   * Dokument an den Zähler; „Hängt an" zeigt danach seinen Pfad. */
  async function handleAcceptMeterSuggestion() {
    if (activeDocument === null) return
    setGeneralError(undefined)
    try {
      const result = await acceptMeterSuggestionMutation.mutateAsync({
        documentId: activeDocument.id,
        ifMatch: String(activeDocument.version),
      })
      applyDocumentSnapshot(result)
    } catch (error) {
      setGeneralError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  async function handleDismissMeterSuggestion() {
    if (activeDocument === null) return
    setGeneralError(undefined)
    try {
      const result = await dismissMeterSuggestionMutation.mutateAsync({
        documentId: activeDocument.id,
        ifMatch: String(activeDocument.version),
      })
      applyDocumentSnapshot(result)
    } catch (error) {
      setGeneralError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  function handleSenderCreated(created: SenderRow) {
    setSenderSelection({ id: created.id, label: created.name })
    setSenderTouched(true)
    setSenderSheetState(null)
  }

  const headSubject = activeDocument !== null ? documentSubjectLabel(activeDocument.subject) : (fixedSubjectLabel ?? '')
  const searchabilityHint = activeDocument !== null ? documentSearchabilityHint(activeDocument) : null
  const suggestion = activeDocument?.suggestion ?? null
  const isSuggestionPending = acceptSuggestionMutation.isPending || dismissSuggestionMutation.isPending
  const meterSuggestion = activeDocument?.meter_suggestion ?? null
  const isMeterSuggestionPending = acceptMeterSuggestionMutation.isPending || dismissMeterSuggestionMutation.isPending

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          showCloseButton={false}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            titleRef.current?.focus()
          }}
          className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
        >
          <form
            data-testid="document-sheet"
            onSubmit={(event) => void handleSubmit(event)}
            className="flex h-full flex-col"
          >
            <SheetHeader>
              <SheetTitle className="text-section font-(--font-weight-section) text-text">
                {isEdit ? 'Dokument ändern' : 'Dokument ablegen'}
              </SheetTitle>
            </SheetHeader>

            <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
              {isEdit && suggestion !== null && (
                <SuggestionCard
                  suggestion={suggestion}
                  isPending={isSuggestionPending}
                  onAccept={() => void handleAcceptSuggestion()}
                  onDismiss={() => void handleDismissSuggestion()}
                />
              )}
              {isEdit && meterSuggestion !== null && (
                <MeterSuggestionCard
                  suggestion={meterSuggestion}
                  isPending={isMeterSuggestionPending}
                  onAccept={() => void handleAcceptMeterSuggestion()}
                  onDismiss={() => void handleDismissMeterSuggestion()}
                />
              )}
              {isEdit && suggestion === null && suggestionNotice !== undefined && (
                <p data-testid="document-suggestion-notice" className="text-label text-muted">
                  {suggestionNotice}
                </p>
              )}

              {isEdit && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {searchabilityHint !== null && (
                    <span
                      data-testid="document-text-source-hint"
                      className="border-border text-label text-muted rounded-card border px-2 py-0.5"
                    >
                      {searchabilityHint}
                    </span>
                  )}
                  <button
                    type="button"
                    data-testid="document-preview"
                    onClick={() => void handlePreview()}
                    disabled={downloadUrlMutation.isPending}
                    className="text-accent text-label hover:underline"
                  >
                    {documentPreviewLabel(document.mime_type)}
                  </button>
                </div>
              )}

              {!isEdit && (
                <div
                  data-testid="document-dropzone"
                  onDragOver={(event) => {
                    event.preventDefault()
                  }}
                  onDrop={(event) => {
                    event.preventDefault()
                    const dropped = event.dataTransfer.files[0]
                    if (dropped !== undefined) handleFile(dropped)
                  }}
                  className="border-border rounded-card flex flex-col items-center justify-center gap-1 border border-dashed px-4 py-8 text-center"
                >
                  <label
                    htmlFor="document-file"
                    className="text-accent text-label inline-flex min-h-(--tap-min) cursor-pointer items-center hover:underline"
                  >
                    Datei wählen
                  </label>
                  <span className="text-label text-muted">oder hierher ziehen</span>
                  <input
                    id="document-file"
                    data-testid="document-file"
                    type="file"
                    // Ohne Vorgabe zeigt der native Dateidialog alles an — die
                    // Richtlinie kommt aus `GET /documents/limits`, nicht als
                    // Aufzählung hier (AK-500). `undefined` vor dem ersten
                    // Antworten lässt den Dialog ungefiltert, statt eine
                    // eigene Übergangsliste zu raten.
                    accept={limits.data?.allowed_mime_types.join(',')}
                    className="sr-only"
                    onChange={(event) => {
                      const selected = event.target.files?.[0]
                      if (selected !== undefined) handleFile(selected)
                    }}
                  />
                  {file !== null && <span className="text-label text-text mt-1 truncate">{file.name}</span>}
                </div>
              )}

              <FormField
                ref={titleRef}
                label="Titel"
                testId="document-title"
                apiField="title"
                errorPrefix="document"
                required
                value={form.title}
                onChange={updateField('title')}
                error={fieldError}
              />

              {subjectEditable ? (
                <DocumentSubjectField
                  value={subjectSelection}
                  onSelect={setSubjectSelection}
                  error={fieldError}
                />
              ) : (
                <div className="flex flex-col gap-1">
                  <span className="text-label text-muted">Hängt an</span>
                  <p data-testid="document-subject" className={`text-text ${FIELD_CLASS}`}>
                    {headSubject}
                  </p>
                </div>
              )}

              <details
                open={isMoreOpen}
                onToggle={(event) => {
                  setIsMoreOpen(event.currentTarget.open)
                }}
              >
                <summary
                  data-testid="document-more"
                  className="text-label text-muted min-h-(--tap-min) cursor-pointer list-item py-2"
                >
                  Weitere Angaben
                </summary>

                <div className="flex flex-col gap-4 pt-2">
                  <SenderField
                    value={senderSelection}
                    legacyText={legacyText}
                    onSelect={(selection) => {
                      setSenderSelection(selection)
                      setSenderTouched(true)
                    }}
                    onRequestNew={(searchText) => {
                      setSenderSheetState({ searchText })
                    }}
                    error={fieldError}
                  />

                  <FormField
                    label="Datum"
                    testId="document-date"
                    apiField="document_date"
                    errorPrefix="document"
                    type="date"
                    value={form.documentDate}
                    onChange={updateField('documentDate')}
                    error={fieldError}
                  />

                  <FormField
                    label="Betrag"
                    testId="document-amount"
                    apiField="amount_cents"
                    errorPrefix="document"
                    inputMode="decimal"
                    value={form.amount}
                    onChange={updateField('amount')}
                    error={fieldError}
                  />

                  <EnumSelectField
                    label="Art"
                    testId="document-category"
                    apiField="category"
                    errorPrefix="document"
                    options={categories.data ?? []}
                    isPending={categories.isPending}
                    value={form.category}
                    onChange={updateField('category')}
                    error={fieldError}
                    placeholder="Ohne Art"
                  />
                </div>
              </details>

              {isEdit && !isCommentsForbidden && (
                <DocumentComments
                  data={comments.data}
                  isError={comments.isError}
                  onRetry={() => void comments.refetch()}
                  onSend={(body, idempotencyKey) =>
                    createCommentMutation.mutateAsync({ documentId: commentsDocumentId, body, idempotencyKey })
                  }
                  onDelete={(commentId) => deleteCommentMutation.mutateAsync({ documentId: commentsDocumentId, commentId })}
                />
              )}
            </div>

            <SheetFooter>
              {generalError !== undefined && (
                <p data-testid="document-error" role="alert" className="text-label text-status-overdue">
                  {generalError}
                </p>
              )}
              {!isEdit && uploadFailed && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-label text-status-overdue">Die Datei ist nicht angekommen.</span>
                  <button
                    type="button"
                    data-testid="document-upload-retry"
                    onClick={() => void submit(false)}
                    className={`text-text px-4 ${FIELD_CLASS}`}
                  >
                    Erneut hochladen
                  </button>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-2">
                {isEdit && (
                  <button
                    type="button"
                    data-testid="document-archive"
                    onClick={() => {
                      setIsArchiveConfirmPending(true)
                    }}
                    disabled={archiveMutation.isPending}
                    className={`text-text px-4 ${FIELD_CLASS} disabled:opacity-60`}
                  >
                    Archivieren
                  </button>
                )}
                <div className="ml-auto flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    data-testid="document-cancel"
                    onClick={() => {
                      onOpenChange(false)
                    }}
                    className={`text-text px-4 ${FIELD_CLASS}`}
                  >
                    Abbrechen
                  </button>
                  {!isEdit && (
                    <button
                      type="button"
                      data-testid="document-save-and-next"
                      disabled={!canSubmit || isPending}
                      aria-busy={isPending}
                      onClick={() => void submit(true)}
                      className={`text-text px-4 ${FIELD_CLASS} disabled:opacity-60`}
                    >
                      Ablegen und noch eins
                    </button>
                  )}
                  <button
                    type="submit"
                    data-testid="document-save"
                    disabled={!canSubmit || isPending}
                    aria-busy={isPending}
                    className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
                  >
                    {isEdit ? 'Speichern' : 'Ablegen'}
                  </button>
                </div>
              </div>
            </SheetFooter>
          </form>

          {/* Innerhalb von `SheetContent`, nicht als Geschwister der Route —
           * dieselbe Begründung wie `AccessSheet`: Radix' `Dialog` markiert bei
           * offenem Modal alles außerhalb seines eigenen Portals als
           * `aria-hidden`/`pointer-events: none`, sonst nähme „Abbrechen"/
           * „Archivieren" keinen Klick an. */}
          <ConfirmationStrip
            testId="document-archive-confirmation"
            message={isArchiveConfirmPending && activeDocument !== null ? `„${activeDocument.title}" archivieren?` : null}
            onDismiss={() => {
              setIsArchiveConfirmPending(false)
            }}
            confirm={{
              confirmLabel: 'Archivieren',
              confirmTestId: 'document-archive-confirm',
              cancelLabel: 'Abbrechen',
              cancelTestId: 'document-archive-cancel',
              onConfirm: () => void handleArchive(),
            }}
          />
        </SheetContent>
      </Sheet>

      {/* Als Geschwister des äußeren `Sheet`, nicht darin verschachtelt —
       * dieselbe Bauform wie `ContractCreateSheet`/`TenantCreateSheet` in
       * `routes/ObjectDetail.tsx`: Zwei unabhängige Radix-`Dialog`s stapeln
       * sich sauber, solange keiner im Portal des anderen hängt. Aus
       * `SenderField`s „Neuer Absender …" heraus (Oberfläche C) — beide
       * Bausteine liegen in `features/documents/`, deshalb direkt eingebunden
       * statt über einen `onRequestNew`-Umweg nach außen. */}
      <SenderSheet
        open={senderSheetState !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setSenderSheetState(null)
        }}
        sender={null}
        initialName={senderSheetState?.searchText ?? ''}
        onCreated={handleSenderCreated}
      />
    </>
  )
}
